import React, { useState, useEffect, useRef } from "react";
import { createContact } from "../../services/contact.service";
import "../../styles/PagesStyles/Contact.css";
import contactBg from "../../assets/images/contact us/contact-hero.jpg";
import "flag-icons/css/flag-icons.min.css";
import usePageTitle from "../../hooks/usePageTitle";
import { trackEvent } from "../../utils/google-analytics";

// Leaflet CSS
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix for default markers in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});


// Custom marker icon using your PNG image
const createCustomIcon = () => {
  // First, try to use the imported image directly
  const iconUrl = require('../../assets/images/contact us/location.png');

  return new L.Icon({
    iconUrl: iconUrl,
    iconRetinaUrl: iconUrl,
    iconSize: [40, 40],  // Adjust size as needed
    iconAnchor: [20, 40], // Center bottom
    popupAnchor: [0, -40],
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
    shadowSize: [41, 41],
    shadowAnchor: [12, 41],
    className: 'custom-marker-icon' // Add custom class
  });
};

const Contact = () => {
  usePageTitle("Combat Solutions AI | Contact");
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [activeOffice, setActiveOffice] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };


  const officeLocations = [
    {
      id: 1,
      name: "Nashik Office",
      lat: 19.9975,
      lng: 73.7898,
      address: "Combat Solutions, Kapadia Commercial Complex, 2nd Floor office No. 25/26, Opposite Kalika Mandir, Gadkari Chowk, Near Tata Motors, Nashik, Maharashtra 422002.",
      phone: "+91 99603 18737",
      email: "enquiry@combatsolutions.co.in",
      country: "India",
      timezone: "IST (UTC+5:30)",
    },
    {
      id: 2,
      name: "Navi Mumbai Office",
      lat: 19.033,
      lng: 73.0297,
      address: "Shop No. 1, Silver Park CHS, Sector No- 36, Kamothe, Navi Mumbai, Maharashtra - 410209",
      phone: "+91 98702 13651",
      email: "enquiry@combatsolutions.co.in",
      country: "India",
      timezone: "IST (UTC+5:30)",
    },
    {
      id: 3,
      name: "Sydney Office (AdaInn)",
      lat: -33.8869,
      lng: 151.2108,
      address: "Level 1, 457-459 Elizabeth Street, Surry Hills, NSW 2010, Sydney, Australia",
      phone: "+61 403 975 240",
      email: "hello@adainn.com",
      country: "Australia",
      timezone: "AEDT (UTC+11)",
    },
  ];

  // Initialize Leaflet Map
  useEffect(() => {
    if (!mapRef.current || map) return;

    const initMap = () => {
      try {
        // Initialize map
        const mapInstance = L.map(mapRef.current, {
          center: [20, 80],
          zoom: 3,
          minZoom: 2,
          maxZoom: 18,
          zoomControl: true,
          scrollWheelZoom: true,
          doubleClickZoom: true,
          touchZoom: true,
          dragging: true,
          attributionControl: false, // Hide attribution for now
        });

        // Add tile layer
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 19,
        }).addTo(mapInstance);

        // Create bounds for all markers
        const bounds = L.latLngBounds([]);
        const customIcon = createCustomIcon();

        // Add markers for each office
        officeLocations.forEach((office) => {
          const marker = L.marker([office.lat, office.lng], {
            icon: customIcon,
            title: office.name,
            alt: office.name,
            riseOnHover: true,
          }).addTo(mapInstance);

          // Create popup content
          const popupContent = `
            <div style="padding: 10px; min-width: 250px; font-family: Arial, sans-serif;">
              <h3 style="margin: 0 0 8px; color: #FF8C00; font-weight: 600; font-size: 16px;">
                ${office.name}
              </h3>
              <p style="margin: 0 0 6px; font-size: 14px; color: #333; line-height: 1.4;">
                ${office.address}
              </p>
              <p style="margin: 0 0 4px; font-size: 14px; color: #333;">
                <strong>Phone:</strong> ${office.phone || 'N/A'}
              </p>
              <p style="margin: 0; font-size: 13px; color: #666; border-top: 1px solid #eee; padding-top: 5px;">
                <strong>${office.country}</strong> • ${office.timezone}
              </p>
            </div>
          `;

          marker.bindPopup(popupContent);

          marker.on('click', () => {
            setActiveOffice(office.id);
            marker.openPopup();
          });

          // Add to bounds for fitting
          bounds.extend([office.lat, office.lng]);
        });

        // Fit map to show all markers with padding
        if (officeLocations.length > 0) {
          if (officeLocations.length === 1) {
            mapInstance.setView([officeLocations[0].lat, officeLocations[0].lng], 12);
          } else {
            mapInstance.fitBounds(bounds, {
              padding: [50, 50],
              maxZoom: 10,
            });
          }
        }

        // Set the map instance
        setMap(mapInstance);
        setMapReady(true);

        // Invalidate size after a short delay to ensure proper rendering
        setTimeout(() => {
          mapInstance.invalidateSize();
        }, 100);

      } catch (error) {
        console.error("Error initializing map:", error);
        setMapReady(false);
      }
    };


    // Small delay to ensure DOM is ready
    const timer = setTimeout(initMap, 100);

    return () => {
      clearTimeout(timer);
      if (map) {
        map.remove();
        setMap(null);
      }
    };
  }, []); // Empty dependency array

  const focusOnOffice = (officeId) => {
    if (!map) return;

    const office = officeLocations.find(o => o.id === officeId);
    if (office) {
      setActiveOffice(officeId);

      // Fly to the office location
      map.flyTo([office.lat, office.lng], 14, {
        duration: 1.5,
      });

      // Open popup after animation
      setTimeout(() => {
        map.eachLayer((layer) => {
          if (layer instanceof L.Marker) {
            const markerPos = layer.getLatLng();
            if (markerPos.lat === office.lat && markerPos.lng === office.lng) {
              layer.openPopup();
            }
          }
        });
      }, 1600);
    }
  };

  const showAllOffices = () => {
    if (!map) return;

    const bounds = L.latLngBounds([]);
    officeLocations.forEach(office => {
      bounds.extend([office.lat, office.lng]);
    });

    map.flyToBounds(bounds, {
      padding: [50, 50],
      maxZoom: 10,
      duration: 1.5,
    });

    setActiveOffice(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    trackEvent('cta_click', {
      page: 'contact page',
      button_text: 'Contact Us',

    })

    try {
      await createContact({
        contact_name: formData.name.trim(),
        contact_email: formData.email.trim(),
        contact_message: formData.message.trim(),
      });
      // ✅ 2. TRACK SUCCESS
      trackEvent("form_submit_success", {
        page: "Contact Page",
      });


      alert("Thank you for your message! We'll get back to you soon.");

      setFormData({
        name: "",
        email: "",
        message: "",
      });
    } catch (err) {

      trackEvent("form_submit_error", {
        page: "Contact Page",
        error_message:
          err?.response?.data?.message || "Unknown error",
      });
      setError(
        err?.response?.data?.message ||
        "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    trackEvent("page_view", {
      page: "Contact Page",
    });
  }, []);

  return (
    <section className="contact-section" id="contact">
      {/* HEADER */}
      <div
        className="contact-header"
        style={{ backgroundImage: `url(${contactBg})` }}
      >
        <div className="contact-header-overlay">
          <h1>Contact Us</h1>
          <p>
            Let's discuss your requirements and explore how we can build secure,
            production ready solutions together.
          </p>
        </div>
      </div>

      {/* FORM AND MAP SIDE BY SIDE */}
      <div className="contact-main-section">
        <div className="contact-main-container">
          {/* LEFT: CONTACT FORM */}
          <div className="contact-form-wrapper">
            <form className="contact-form" onSubmit={handleSubmit}>
              <h2>Get In Touch</h2>

              {error && <p className="form-error">{error}</p>}

              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={handleChange}
                  onFocus={() =>
                    trackEvent("form_field_focus", {
                      page: "Contact Page",
                      field_name: "Name",
                    })
                  }
                  required
                />

              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter your email@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() =>
                    trackEvent("form_field_focus", {
                      page: "Contact Page",
                      field_name: "Email",
                    })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  placeholder="Tell us about your project or inquiry..."
                  value={formData.message}
                  onChange={handleChange}
                  onFocus={() =>
                    trackEvent("form_field_focus", {
                      page: 'Contact page',
                      field_name: 'message'
                    })
                  }
                  required
                />
              </div>

              <button type="submit" className="contact-btn" disabled={loading}>
                {loading ? "Sending..." : "Contact Us →"}
              </button>
            </form>
          </div>

          {/* RIGHT: MAP WITH CONTROLS ABOVE */}
          <div className="contact-map-wrapper">
            {/* Map Controls Panel */}
            <div className="map-controls-panel">
              <div className="map-controls">
                <button
                  onClick={showAllOffices}
                  className={`map-control-btn ${!activeOffice ? 'active' : ''}`}
                >
                  Show All Offices
                </button>
                {officeLocations.map((office) => (
                  <button
                    key={office.id}
                    onClick={() => focusOnOffice(office.id)}
                    className={`map-control-btn ${activeOffice === office.id ? 'active' : ''}`}
                  >
                    {office.name.split(' ')[0]}
                  </button>
                ))}
              </div>
            </div>

            {/* Map Container */}
            <div className="contact-map">
              <div
                ref={mapRef}
                className="leaflet-map"
                style={{ height: "100%", width: "100%" }}
              />

              {!mapReady && (
                <div className="map-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading interactive map...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* OFFICE ADDRESSES SECTION - BELOW FORM AND MAP */}
      <div className="contact-addresses-section">
        <div className="contact-addresses-container">
          <div className="contact-addresses-plain">
            {officeLocations.map((office) => (
              <div className="address-item-plain" key={office.id}>
                {/*------------------country block--------------- */}
                <div className="office-country-top">
                  <span
                    className={`fi fi-${office.country === "India" ? "in" : "au"} country-flag-icon`}
                    aria-label={office.country}
                  ></span>
                </div>


                <div className="office-info">
                  <h4 className="office-title">{office.name}</h4>
                  <p className="office-address">{office.address}</p>
                </div>

                <div className="office-contact-details">
                  <div className="office-contact">
                    {office.phone && (
                      <a href={`tel:${office.phone}`} className="phone">
                        {office.phone}
                      </a>
                    )}

                    {office.phone && office.email && (
                      <span className="divider"> | </span>
                    )}

                    <a href={`mailto:${office.email}`} className="email-text">
                      {office.email}
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;